import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { OverlayModule, ConnectedPosition, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  attributeLabel,
  MESSAGE_ATTRIBUTE_KEYS,
} from '../../../../utils/utils';
import { ariaOwnsAdd, ariaOwnsRemove } from '../modal-component/modal-component.component';

let nextPanelId = 0;

/**
 * Attribute picker shown beside the message textarea. Clicking the trigger opens
 * a menu of contact attributes; selecting one emits its key so the parent can
 * insert the `{{attribute}}` placeholder at the cursor.
 *
 * The menu is positioned with the CDK overlay (cdkConnectedOverlay), so it stays
 * anchored to the trigger, follows it on scroll, flips when there's no room
 * below, and is never clipped by ancestor `overflow:hidden` / stacking contexts.
 * The overlay lives on <body>, outside the app's `.dark` wrapper, so the panel
 * carries the theme class itself.
 */
@Component({
  selector: 'message-attribute-menu',
  standalone: true,
  imports: [OverlayModule, MatTooltipModule],
  template: `
    <button
      type="button"
      class="mam__trigger"
      [class.is-open]="open"
      cdkOverlayOrigin
      #trigger="cdkOverlayOrigin"
      (click)="toggle(trigger)"
      [attr.aria-label]="'Insert ' + heading.toLowerCase()"
      [matTooltip]="'Insert ' + heading.toLowerCase()"
      aria-haspopup="menu"
      [attr.aria-expanded]="open"
      [attr.aria-controls]="open ? panelId : null"
    >
      <span class="bk-sym" aria-hidden="true">data_object</span>
    </button>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="open"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      (backdropClick)="close(false)"
      (detach)="close()"
      (attach)="onAttach()"
    >
      <div
        #panel
        class="mam__panel"
        [id]="panelId"
        [class.dark]="isDark"
        role="menu"
        [attr.aria-label]="heading"
        (keydown)="onMenuKeydown($event)"
      >
        <p class="mam__head">
          <span class="bk-sym" aria-hidden="true">{{ headingIcon }}</span>
          {{ heading }}
        </p>

        <div class="mam__list">
          @for (key of attributeKeys; track key) {
          <button
            type="button"
            role="menuitem"
            class="mam__item"
            (click)="pick(key)"
          >
            {{ labelFor(key) }}
          </button>
          }
        </div>
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .mam__trigger {
      display: inline-grid;
      place-items: center;
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 1px solid var(--bk-line);
      border-radius: var(--bk-r-control);
      background: transparent;
      color: var(--bk-ash);
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition:
        background-color 140ms ease,
        border-color 140ms ease,
        color 140ms ease,
        transform 140ms var(--bk-ease-out);
    }

    .mam__trigger .bk-sym {
      font-size: 19px;
    }

    .mam__trigger.is-open {
      background: var(--bk-active);
      border-color: var(--bk-line-strong);
      color: var(--bk-ink);
    }

    .mam__trigger:active {
      transform: scale(0.94);
    }

    .mam__trigger:focus-visible {
      outline: 2px solid var(--bk-ink);
      outline-offset: 2px;
    }

    @media (hover: hover) and (pointer: fine) {
      .mam__trigger:hover {
        background: var(--bk-hover);
        border-color: var(--bk-line-strong);
        color: var(--bk-ink);
      }
    }

    .mam__panel {
      width: 232px;
      padding: 6px;
      background: var(--bk-panel);
      border: 1px solid var(--bk-line);
      border-radius: var(--bk-r-tile);
      box-shadow: 0 18px 44px -18px rgb(0 0 0 / 0.45), 0 4px 12px -6px rgb(0 0 0 / 0.2);
      color: var(--bk-ink);
      font-family: var(--bk-font);
      transform-origin: top right;
      animation: mam-in 150ms var(--bk-ease-out) both;
    }

    .mam__head {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      padding: 8px 10px 6px;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--bk-faint);
    }

    .mam__head .bk-sym {
      font-size: 15px;
    }

    .mam__list {
      display: flex;
      flex-direction: column;
      gap: 1px;
      max-height: 256px;
      overflow-y: auto;
    }

    .mam__item {
      display: flex;
      align-items: center;
      width: 100%;
      min-height: 36px;
      padding: 0 10px;
      border: none;
      border-radius: 10px;
      background: transparent;
      font-family: var(--bk-font);
      font-size: 13px;
      font-weight: 500;
      text-align: left;
      color: var(--bk-ink);
      cursor: pointer;
      transition: background-color 120ms ease;
    }

    .mam__item:focus-visible {
      outline: 2px solid var(--bk-ink);
      outline-offset: -2px;
    }

    @media (hover: hover) and (pointer: fine) {
      .mam__item:hover {
        background: var(--bk-hover);
      }
    }

    @keyframes mam-in {
      from {
        opacity: 0;
        transform: scale(0.96);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .mam__trigger:active {
        transform: none;
      }

      .mam__panel {
        animation-name: mam-fade;
      }

      @keyframes mam-fade {
        from {
          opacity: 0;
        }
      }
    }
  `,
})
export class MessageAttributeMenuComponent {
  /** Emits the selected attribute key (e.g. 'firstName'). */
  @Output() select = new EventEmitter<string>();

  /**
   * The attribute keys offered in the menu. Defaults to the fixed contact
   * attributes; pass a dynamic list (e.g. columns parsed from an uploaded file)
   * to repurpose the picker for arbitrary merge fields.
   */
  @Input() attributeKeys: string[] = MESSAGE_ATTRIBUTE_KEYS;

  /** Optional friendly-label overrides keyed by attribute key. */
  @Input() labels: Record<string, string> | null = null;

  /** Menu heading + leading material icon (defaults suit contact attributes). */
  @Input() heading: string = 'Contact attribute';
  @Input() headingIcon: string = 'person';

  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;

  open = false;
  isDark = false;
  readonly panelId = `mam-panel-${nextPanelId++}`;
  private origin?: CdkOverlayOrigin;
  /** The dialog the trigger sits in, which claims the panel while it is open. */
  private ownerDialog: Element | null = null;

  // Anchor below the trigger, right-aligned; flip above when there's no room.
  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  toggle(origin?: CdkOverlayOrigin) {
    if (origin) this.origin = origin;
    if (!this.open) {
      const trigger = this.origin?.elementRef.nativeElement as HTMLElement | undefined;
      this.isDark = !!trigger?.closest('.dark');
    }
    this.open = !this.open;
  }

  /** Close the menu; keyboard users land back on the trigger. */
  close(returnFocus: boolean = true) {
    if (!this.open) return;
    const focusWasInside = !!this.panel?.nativeElement.contains(document.activeElement);
    this.open = false;
    ariaOwnsRemove(this.ownerDialog, this.panelId);
    this.ownerDialog = null;
    if (returnFocus && focusWasInside) {
      (this.origin?.elementRef.nativeElement as HTMLElement | undefined)?.focus();
    }
  }

  pick(key: string) {
    this.select.emit(key);
    this.close(false);
  }

  onAttach() {
    // The panel renders on <body>, outside any dialog. A dialog marked
    // aria-modal hides everything outside it from screen readers, so the
    // dialog the trigger sits in claims the panel (as MatSelect does).
    const trigger = this.origin?.elementRef.nativeElement as HTMLElement | undefined;
    this.ownerDialog = trigger?.closest('[aria-modal="true"]') ?? null;
    ariaOwnsAdd(this.ownerDialog, this.panelId);
    this.focusFirst();
  }

  focusFirst() {
    // The panel renders on attach; wait a frame so it is in the DOM.
    setTimeout(() => this.items()[0]?.focus());
  }

  onMenuKeydown(event: Event) {
    const key = (event as KeyboardEvent).key;
    if (key === 'Escape') {
      // Handled here (before the overlay's own listener) so focus can go back
      // to the trigger while the panel still holds it.
      event.preventDefault();
      this.close();
      return;
    }
    const items = this.items();
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % items.length;
    else if (key === 'ArrowUp') next = current <= 0 ? items.length - 1 : current - 1;
    else if (key === 'Home') next = 0;
    else if (key === 'End') next = items.length - 1;
    else if (key === 'Tab') {
      // The panel sits at the end of <body>; letting Tab run would send focus
      // out of the page (and out of a surrounding dialog's focus trap).
      // Close and go back to the trigger; the next Tab moves on from there.
      event.preventDefault();
      this.close(true);
      return;
    }
    if (next < 0) return;
    event.preventDefault();
    items[next].focus();
  }

  labelFor(key: string): string {
    return this.labels?.[key] ?? attributeLabel(key);
  }

  private items(): HTMLElement[] {
    const panel = this.panel?.nativeElement;
    return panel ? Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }
}
